import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function InventoryManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [error, setError] = useState("");

  const db = getFirestore();
  const navigate = useNavigate();

  const collections = [
    "DesignerMetal&MetalFrame",
    "FrameLess",
    "FullFrame",
    "HalfFrame",
    "SafetyGlasses",
    "Sunglassess",
    "Kids-Frame",
    "Kids-Sunglass",
  ];

  // Fetch all products from collections
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let allProducts = [];
        for (const collectionName of collections) {
          const colRef = collection(db, collectionName);
          const querySnapshot = await getDocs(colRef);
          querySnapshot.forEach((doc) => {
            allProducts.push({ id: doc.id, collection: collectionName, ...doc.data() });
          });
        }
        setProducts(allProducts);
        setFilteredProducts(allProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError("Failed to load products.");
      }
    };
    fetchProducts();
  }, []);

  // Handle search by ProductID
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter((product) =>
        product.ProductID.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  // Handle status change
  const handleStatusChange = async (product, newStatus) => {
    try {
      const productRef = doc(db, product.collection, product.id);
      await setDoc(productRef, { Status: newStatus }, { merge: true });

      // Update local state
      setProducts(
        products.map((p) => (p.id === product.id ? { ...p, Status: newStatus } : p))
      );
      setFilteredProducts(
        filteredProducts.map((p) => (p.id === product.id ? { ...p, Status: newStatus } : p))
      );
      setError("Status updated successfully.");
    } catch (error) {
      console.error("Error updating status:", error);
      setError("Failed to update status.");
    }
  };

  // Handle delete product
  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Are you sure you want to delete ${product.Name}?`)) {
      try {
        const productRef = doc(db, product.collection, product.id);
        await deleteDoc(productRef);

        // Update local state
        setProducts(products.filter((p) => p.id !== product.id));
        setFilteredProducts(filteredProducts.filter((p) => p.id !== product.id));
        setError("Product deleted successfully.");
      } catch (error) {
        console.error("Error deleting product:", error);
        setError("Failed to delete product.");
      }
    }
  };

  // Handle view product details
  const handleViewProduct = (product) => {
    navigate(`/product-details`, { state: product });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Search Box */}
      <div className="flex justify-center mb-8 mt-8 px-4">
        <div className="relative w-full max-w-lg">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Product ID..."
            className="w-full px-4 py-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 text-gray-800 text-sm bg-white"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-center text-red-600 mb-6 font-medium">
          {error}
        </div>
      )}

      {/* Products Grid */}
      <div className="container mx-auto px-4 pb-12">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">Inventory Management</h1>
        {filteredProducts.length === 0 ? (
          <p className="text-center text-gray-600">No products found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <img
                  src={product.ImageUrl1}
                  alt={product.Name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <h2 className="text-lg font-semibold text-gray-800 truncate">
                    {product.Name}
                  </h2>
                  <p className="text-sm text-gray-600">ID: {product.ProductID}</p>
                  <p className="text-sm text-gray-600">Category: {product.collection}</p>
                  <p className="text-sm text-gray-600">Cost: ₹{product.Cost}</p>
                  <div className="mt-3">
                    <select
                      value={product.Status}
                      onChange={(e) => handleStatusChange(product, e.target.value)}
                      className="w-full px-2 py-1 border border-gray-400 rounded-md text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-gray-600 bg-white"
                    >
                      <option value="Available">Available</option>
                      <option value="Sold">Sold</option>
                    </select>
                  </div>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => handleViewProduct(product)}
                      className="flex-1 bg-gray-800 text-white py-2 rounded-md hover:bg-gray-900 transition-colors text-sm"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product)}
                      className="flex-1 bg-red-600 text-white py-2 rounded-md hover:bg-red-700 transition-colors text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}